/**
 * ambiente-tag.js — identificação de ambiente por cortina.
 *
 * Duas metades, no mesmo padrão de assets/whatsapp-cta.js:
 *  1. Funções puras (testadas em tests/ambiente-tag.test.js via `node --test`)
 *  2. O custom element <ambiente-picker>, que só roda no navegador (guardado por `typeof document`)
 */
(function (root) {
  'use strict';

  var SUGESTOES = ['Sala', 'Quarto', 'Suíte', 'Cozinha', 'Escritório'];
  var MAX = 24;

  /** Forma final que vai virar o valor da property. */
  function normalizar(txt) {
    return String(txt == null ? '' : txt)
      .replace(/[<>]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, MAX)
      .trim();
  }

  /**
   * Forma comparável: minúscula e sem acento. Existe porque o carrinho pode ter
   * 'suite' digitado à mão enquanto o chip oferece 'Suíte' — sem isso o cliente
   * ganharia dois ambientes que a produção lê como o mesmo cômodo.
   */
  function chave(txt) {
    return normalizar(txt)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * 'Quarto' + []                    → 'Quarto'
   * 'Quarto' + ['Quarto']            → 'Quarto 2'
   * 'Quarto' + ['Quarto','Quarto 2'] → 'Quarto 3'
   */
  function proximoNome(base, ocupados) {
    var nome = normalizar(base);
    if (!nome) return '';

    var usados = Object.create(null);
    (ocupados || []).forEach(function (o) {
      var k = chave(o);
      if (k) usados[k] = true;
    });

    if (!usados[chave(nome)]) return nome;

    for (var n = 2; n <= 99; n++) {
      var sufixo = ' ' + n;
      // Apara a base para o sufixo caber nos 24 caracteres — sem isso um nome
      // longo perderia o número no slice e o loop devolveria sempre o mesmo valor.
      var tentativa = nome.slice(0, MAX - sufixo.length).trim() + sufixo;
      if (!usados[chave(tentativa)]) return tentativa;
    }
    return nome;
  }

  /**
   * Valor final gravado quando o texto livre é confirmado (blur do input de
   * "+ Outro"), já com a numeração aplicada — ao contrário de `digitou()`,
   * que só normaliza a cada tecla. Extraída como função pura (em vez de
   * ficar só dentro do custom element) para poder ser testada sem DOM.
   */
  function confirmarTextoLivre(valorDigitado, ocupados) {
    return proximoNome(valorDigitado, ocupados);
  }

  var api = {
    SUGESTOES: SUGESTOES,
    MAX: MAX,
    normalizar: normalizar,
    chave: chave,
    proximoNome: proximoNome,
    confirmarTextoLivre: confirmarTextoLivre,
  };

  /**
   * Ambientes já em uso no carrinho. Só é chamado quando o passo de resumo abre —
   * quem abandona antes disso não paga o request. Falha de rede degrada para
   * lista vazia: o campo continua funcionando, só sem numeração automática.
   */
  function lerOcupados() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return (cart.items || [])
          .map(function (i) { return (i.properties || {})['Ambiente']; })
          .filter(Boolean);
      })
      .catch(function () { return []; });
  }

  api.lerOcupados = lerOcupados;

  if (typeof document !== 'undefined' && typeof customElements !== 'undefined' &&
      !customElements.get('ambiente-picker')) {
    class AmbientePicker extends HTMLElement {
      connectedCallback() {
        if (this._pronto) return; // morph do tema pode reconectar o mesmo nó
        this._pronto = true;
        this._valor = '';
        this._base = ''; // nome "cru" por trás do _valor numerado — chip clicado ou texto confirmado
        this._ocupados = [];

        this.chips = Array.from(this.querySelectorAll('[data-ambiente-chip]'));
        this.outroBtn = this.querySelector('[data-ambiente-outro]');
        this.input = this.querySelector('[data-ambiente-input]');
        // Nome "hiddenInput", não "hidden": a IDL HTMLElement.hidden já existe no protótipo
        // do custom element — atribuir a este.hidden reescreveria o atributo hidden="" do
        // próprio <ambiente-picker> (coagido a boolean) e escondia o widget inteiro.
        this.hiddenInput = this.querySelector('[data-ambiente-hidden]');

        this.chips.forEach((chip) => {
          chip.addEventListener('click', () => this.escolherChip(chip));
        });
        if (this.outroBtn) this.outroBtn.addEventListener('click', () => this.alternarOutro());
        if (this.input) {
          this.input.addEventListener('input', () => this.digitou());
          // Numeração só entra no commit (perda de foco), não a cada tecla —
          // ver comentário em confirmarOutro(). Só 'blur' (não também
          // 'change'): os dois disparariam em sequência na mesma perda de
          // foco e a segunda chamada releria o valor já numerado como se
          // fosse a base crua, arrastando "Quarto 2" pra virar "Quarto 2 2"
          // num commit seguinte em vez de "Quarto 3".
          this.input.addEventListener('blur', () => this.confirmarOutro());
        }
      }

      get valor() { return this._valor; }

      /**
       * Chamado pelo consumidor quando o passo de resumo aparece. Refaz a
       * leitura do carrinho e, se já havia uma seleção (drawer some sem
       * recarregar a página — product-form.js:376 usa fetch), reaplica a
       * numeração em cima dela: sem isso, configurar uma segunda cortina
       * igual à primeira reabre com o mesmo nome já usado.
       */
      abrir() {
        return lerOcupados().then((lista) => {
          this._ocupados = lista;
          this.revalidarSelecao();
        });
      }

      revalidarSelecao() {
        if (!this._base) return;
        const nome = proximoNome(this._base, this._ocupados);
        if (nome === this._valor) return;
        if (this.input && !this.input.hidden) this.input.value = nome; // cliente vê o que foi gravado
        this.definir(nome);
      }

      escolherChip(chip) {
        if (chip.getAttribute('aria-pressed') === 'true') { this.limpar(); return; }
        const base = chip.dataset.ambienteChip;
        const nome = proximoNome(base, this._ocupados);
        this.marcar(chip);
        this.fecharOutro();
        this._base = base;
        this.definir(nome);
      }

      alternarOutro() {
        if (this.outroBtn.getAttribute('aria-pressed') === 'true') { this.limpar(); return; }
        this.marcar(this.outroBtn);
        this.input.hidden = false;
        this.input.focus();
        this._base = '';
        this.definir(normalizar(this.input.value));
      }

      digitou() {
        // Texto livre não recebe sufixo automático enquanto digita — só no
        // commit (confirmarOutro). Numerar a cada tecla reescreveria o campo
        // no meio da digitação (a cliente digita "Quarto" e vê "Quarto 2"
        // aparecer antes de terminar a palavra).
        this.definir(normalizar(this.input.value));
      }

      /**
       * Aplica a numeração quando o texto livre é confirmado (blur do input).
       * A mesma digitação que o carrinho e o script do WhatsApp numeram
       * ("Quarto" repetido → "Quarto 2") tem que numerar aqui também —
       * sem isso duas cortinas idênticas saem com a mesma etiqueta.
       */
      confirmarOutro() {
        if (!this.input || this.input.hidden) return;
        this._base = normalizar(this.input.value);
        if (!this._base) { this.input.value = ''; this.definir(''); return; }
        const nome = confirmarTextoLivre(this._base, this._ocupados);
        this.input.value = nome; // o campo visível mostra o que foi realmente gravado
        this.definir(nome);
      }

      marcar(alvo) {
        this.chips.concat([this.outroBtn]).forEach((b) => {
          if (b) b.setAttribute('aria-pressed', String(b === alvo));
        });
      }

      fecharOutro() {
        if (!this.input) return;
        this.input.hidden = true;
        this.input.value = '';
      }

      limpar() {
        this.marcar(null);
        this.fecharOutro();
        this._base = '';
        this.definir('');
      }

      definir(valor) {
        this._valor = valor;
        if (!this.hiddenInput) return;
        this.hiddenInput.value = valor;
        // Input desabilitado não entra no FormData — property em branco nunca
        // chega ao carrinho, e linhas idênticas seguem agrupando como hoje.
        this.hiddenInput.disabled = !valor;
      }
    }
    customElements.define('ambiente-picker', AmbientePicker);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof root !== 'undefined') root.PromacalAmbiente = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
